"use client";
import * as React from "react";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "../primitives/hover-card";
import { cn, extractText } from "../utils";

export interface TruncatedValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The node to render and truncate. Typically plain text; pass `fullText` when
   * the child is not a plain string (e.g. already wrapped in a link).
   */
  children: React.ReactNode;
  /**
   * String to show in the overflow popover. Falls back to the extracted text
   * of `children`.
   */
  fullText?: string;
  /**
   * Max number of visible lines. Defaults to 1 (single-line ellipsis). When
   * greater than 1, applies Tailwind's line-clamp utilities.
   */
  lines?: number;
  /**
   * When false, render plain text with no truncation and no popover.
   */
  truncate?: boolean;
  /**
   * When set, truncate the string content to this many characters (appending
   * an ellipsis) and always show the full text in the hover card. Only applies
   * when `children` resolves to a plain string.
   */
  maxChars?: number;
  className?: string;
}

const LINE_CLAMP_CLASS: Record<number, string> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
};

/**
 * `TruncatedValue` renders a node with single-line ellipsis truncation (or
 * multi-line clamp when `lines > 1`) and, when the content actually overflows,
 * shows the full text in a hover card. If the content fits, no hover card is
 * rendered.
 *
 * Usage:
 * ```tsx
 * <TruncatedValue>{account.title}</TruncatedValue>
 * <TruncatedValue lines={2}>{longDescription}</TruncatedValue>
 * <TruncatedValue truncate={false}>{value}</TruncatedValue>
 * ```
 */
export function TruncatedValue({
  children,
  fullText,
  lines = 1,
  truncate = true,
  maxChars,
  className,
  ...rest
}: TruncatedValueProps) {
  // A callback ref, so a remount re-measures: a `ref.current` snapshot stays
  // bound to the replaced node and its stale "fits" reading never clears.
  const [el, setEl] = React.useState<HTMLSpanElement | null>(null);
  const [overflowing, setOverflowing] = React.useState(false);

  const sourceText = typeof children === "string" ? children : extractText(children);
  const charTruncated = truncate && maxChars !== undefined && sourceText.length > maxChars;
  const displayChildren = charTruncated ? `${sourceText.slice(0, maxChars).trimEnd()}…` : children;
  const resolvedFullText = fullText ?? sourceText;
  const hasExplicitFullText = fullText !== undefined && fullText.trim() !== sourceText.trim();

  React.useEffect(() => {
    if (!truncate) {
      setOverflowing(false);
      return;
    }
    if (!el || !el.isConnected) return;

    const measure = () => {
      if (lines > 1) {
        setOverflowing(el.scrollHeight - el.clientHeight > 1);
      } else {
        setOverflowing(el.scrollWidth - el.clientWidth > 1);
      }
    };

    measure();

    // `ResizeObserver` only fires on the element's own *box-size* changes. The
    // single post-mount `measure()` above can race ahead of the final layout —
    // e.g. cold loads, web-font swap, or an async sibling affordance (a citation
    // badge in the detail sidebar) that narrows the column after first paint.
    // When only the *content* width grows while the box width stays fixed, the
    // observer never fires and the stale "fits" reading sticks, so a visibly
    // truncated value shows no hover popover. Re-measure once the next frame
    // and the document fonts settle to close that gap. (In a hidden tab the rAF
    // is deferred until the tab is shown, which is exactly when it matters.)
    const raf = requestAnimationFrame(measure);
    // Timer fallback: `requestAnimationFrame` is paused in hidden/background
    // tabs, so a value laid out while the tab is in the background would not be
    // re-measured until the rAF resumes. A timer still fires (throttled) and
    // covers that gap as well as a late layout settle.
    const timer = setTimeout(measure, 250);
    let cancelled = false;
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(() => {
        if (!cancelled) measure();
      });
    }

    // `ResizeObserver` is absent in SSR and in jsdom-based tests; fall back to
    // the one-shot + rAF/timer measurement above rather than throwing.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      ro?.disconnect();
    };
  }, [el, truncate, lines, children, fullText]);

  if (!truncate) {
    return (
      <span className={className} {...rest}>
        {children}
      </span>
    );
  }

  const truncateClass =
    lines > 1
      ? cn(LINE_CLAMP_CLASS[lines] ?? "line-clamp-6", "[overflow-wrap:anywhere] break-words")
      : "truncate whitespace-nowrap";

  const spanNode = (
    <span
      ref={setEl}
      className={cn(
        "block w-full min-w-0 max-w-full overflow-hidden text-ellipsis",
        truncateClass,
        className,
      )}
      {...rest}
    >
      {displayChildren}
    </span>
  );

  if (!overflowing && !charTruncated && !hasExplicitFullText) {
    return spanNode;
  }

  const popoverText = resolvedFullText.trim();
  if (!popoverText) {
    return spanNode;
  }

  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>{spanNode}</HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-auto max-w-sm whitespace-normal break-words p-2 text-[13px] leading-snug"
      >
        {popoverText}
      </HoverCardContent>
    </HoverCard>
  );
}
